import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, IconButton, useToast } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { RecaptchaVerifier } from 'firebase/auth';
import InstagramVerificationStep from './components/InstagramVerificationStep';
import { usePhoneVerification } from '../../hooks/usePhoneVerification';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { ff } from '../../theme';

declare global {
  interface Window {
    grecaptcha: any;
    recaptchaVerifier: RecaptchaVerifier;
  }
}

const InstagramVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [user, authLoading] = useAuthState(auth);
  const { sendVerificationCode } = usePhoneVerification();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState<boolean>(false);

  useEffect(() => {
    const recaptchaContainer = document.getElementById('recaptcha-container');
    if (recaptchaContainer) {
      recaptchaContainer.style.visibility = 'hidden';
      setRecaptchaLoaded(true);
    }

    return () => {
      if (recaptchaContainer) {
        recaptchaContainer.style.visibility = 'hidden';
      }
    };
  }, []);
  
  // Get data passed from the application page or profile page
  const { email, instagram, influencerID, phoneNumber, code, returnTo, returnState } = location.state || {};

  // `user` is undefined until Firebase restores the session, so deciding on it during that first
  // render would bounce a signed-in phoneless creator on every reload.
  if (authLoading) {
    return null;
  }

  // If no required data, redirect appropriately based on where user came from. Phone is only
  // needed for the signup path (phone verification follows); collab-invite creators have none.
  if (!email || !instagram || (!user && !phoneNumber)) {
    // If user came from profile, go back to profile; otherwise go to signup
    navigate(-1);
    return null;
  }

  const handleVerificationSuccess = async () => {
    // If user is already authenticated, just redirect them back
    if (user) {
      navigate(-1);
      return;
    }

    // For unauthenticated users (during signup), continue with phone verification
    try {
      await sendVerificationCode(
        phoneNumber,
        recaptchaLoaded,
        returnTo ? { returnTo, state: returnState } : undefined
      );
    } catch (error) {
      console.error('Error sending phone verification code:', error);
      // Error handling is already done in the hook
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box
      minH="100svh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="white"
      px={{ base: '20px', md: 6 }}
      pt={{ base: 4, md: 8 }}
      pb={{ base: 6, md: 10 }}
    >
      <Box width="100%" maxW="440px">
        <IconButton
          aria-label="Back"
          icon={<FiArrowLeft size={20} />}
          onClick={handleBack}
          variant="ghost"
          color={ff.ink}
          minW="44px"
          h="44px"
          ml="-10px"
        />
      </Box>
      <Box width="100%" maxW="440px" flex={1} display="flex" flexDirection="column">
        <InstagramVerificationStep
          email={email}
          instagram={instagram}
          verificationCode={code}
          expectedInfluencerID={code ? influencerID : undefined}
          onVerificationSuccess={handleVerificationSuccess}
          onBack={handleBack}
          isUserAuthenticated={!!user}
        />
      </Box>
    </Box>
  );
};

export default InstagramVerificationPage;
